import type { FC, ReactElement } from 'react';
import { memo, useState } from 'react';

import { isUndefined } from 'lodash'

import {
  type ChecklistItem,
  ChecklistPopover,
  type IChecklistPopoverProps,
} from './ChecklistPopover'

const getChecklistID = (id) => {
  return `checklist.${id}`
}

const getPersistChecklistByID = (id): { [x: string]: boolean } => {
  try {
    return JSON.parse(window.localStorage.getItem(getChecklistID(id)))
  } catch (err) {
    return {}
  }
}

const persistChecklistByID = (id, checklist: ChecklistItem[]) => {
  try {
    const checklistData = checklist.reduce((prev, curr) => {
      prev[curr.value] = curr.checked
      return prev
    }, {})

    window.localStorage.setItem(
      getChecklistID(id),
      JSON.stringify(checklistData)
    )
  } catch (err) {
    return
  }
}

export interface IWithChecklistPopoverProps extends IChecklistPopoverProps {
  id?: string
  persist?: boolean
  children: (context?: {
    checklist: {
      [key: string]: boolean
    }
    checklistMenu: ReactElement
  }) => ReactElement
}

export const WithChecklistPopover: FC<IWithChecklistPopoverProps> =
  memo(
    ({
      checklist: initialChecklist,
      onUpdate,
      persist = false,
      id,
      analyticsId,
      children,
    }) => {
      const checklistFromStorage = getPersistChecklistByID(id)

      const [checklist, setChecklist] = useState<ChecklistItem[]>(
        persist && id
          ? initialChecklist.map((initialChecklistItem) => ({
              ...initialChecklistItem,
              checked: !isUndefined(
                checklistFromStorage?.[initialChecklistItem.value]
              )
                ? checklistFromStorage?.[initialChecklistItem.value]
                : initialChecklistItem.value,
            }))
          : initialChecklist
      )

      const flattenChecklist = (
        list: ChecklistItem[]
      ): (typeof list)[number]['value'] => {
        return list.reduce((prev, item) => {
          prev[item.value] = !!item.checked
          return prev
        }, {})
      }

      const checklistMenu = (
        <ChecklistPopover
          analyticsId={analyticsId}
          checklist={checklist}
          onUpdate={(_, nextState) => {
            if (persist && id) {
              persistChecklistByID(id, nextState)
            }

            setChecklist(nextState)
          }}
        />
      )

      return children({
        checklist: flattenChecklist(checklist),
        checklistMenu,
      })
    }
  )

WithChecklistPopover.defaultProps = {
  checklist: [],
  onUpdate: () => undefined,
}
WithChecklistPopover.displayName = 'WithChecklistPopover'
